import server from 'utils/server';

export type VisitWishStatus =
  | 'pending_confirmation'
  | 'active'
  | 'paused'
  | 'fulfilled'
  | 'withdrawn'
  | 'stopped'
  | 'expired';

export type DeliveryStatus = { status: string; sentAt: number | null } | null;

export type VisitWishAlert = {
  id: string;
  slots: { date: string; occassion: string; time: string }[];
  createdAt: number;
  delivery: { email?: DeliveryStatus; sms?: DeliveryStatus };
};

export type OfferStatus = 'holding' | 'offered' | 'accepting' | 'booked' | 'expired' | 'released' | 'failed';

/** An exclusive offer: a table held for one guest until a deadline. */
export type ExclusiveOffer = {
  id: string;
  status: OfferStatus;
  slot: { date: string; occassion: string; time: string };
  tables: string[];
  durationMinutes: number;
  expiresAt: number | null;
  acceptUntil: number | null;
  upperBound: number;
  createdAt: number;
  endedReason: string | null;
};

export const LIVE_OFFER: OfferStatus[] = ['holding', 'offered', 'accepting'];

/** A visit wish as the API lists it for the team (no tokens, no message content). */
export type VisitWish = {
  id: string;
  status: VisitWishStatus;
  guests: number;
  occassions: string[];
  dateFrom: string;
  dateTo: string;
  weekdays: number[];
  timeFrom?: string | null;
  timeTo?: string | null;
  minLeadHours: number;
  contact: { name: string; email: string; phone?: string | null };
  channels: { email: true; sms: boolean };
  priority: number;
  createdAt: number;
  confirmedAt: number | null;
  lastAlertAt: number | null;
  fulfilledBy: { reservationId: string; at: number } | null;
  staffReservation?: { id: string; date: string; time: string; state: 'preparing' | 'protection_pending' };
  endedBy: string | null;
  alerts: VisitWishAlert[];
  exclusive?: { durationMinutes: number; markedBy: string; markedAt: number } | null;
  activeOfferId?: string | null;
  offers: ExclusiveOffer[];
  /** Technical delivery state of exclusive offers per slot (`date|service|time`); not a guest decline. */
  deliveryHolds?: Record<
    string,
    { reason: 'undeliverable' | 'uncertain'; failures: number; retryAt: number | null; blockedAt: number | null; at: number }
  > | null;
  /** The restaurant's VIP tags this guest carries (matched by email). */
  vipTags: string[];
};

/** The API's bounds for the wave rule, for the restaurant and for each date exception. */
export const WAVE_LIMITS = {
  waveSize: { min: 1, max: 50 },
  waveIntervalMinutes: { min: 1, max: 1440 },
};

export type DateRule = { date: string; usePriority?: boolean; waveSize?: number; waveIntervalMinutes?: number };

/** A date's override: fields left out follow the restaurant standard. */
export type DateRulePatch = Omit<DateRule, 'date'>;

export type VisitWishSettings = {
  /** Derived by the API from the app activation; never edited here. */
  enabled: boolean;
  waveSize: number;
  waveIntervalMinutes: number;
  usePriority: boolean;
  exclusiveOffers: boolean;
  vipTags: string[];
  vipPriority: number;
  dateRules: DateRule[];
  /**
   * The published revision an edit is based on. A draft carries it unchanged
   * to the API, which refuses it once another publish happened meanwhile.
   */
  revision?: number;
};

/** The published settings, with the app activation and their revision. */
export type LiveVisitWishSettings = VisitWishSettings & { appEnabled: boolean; revision: number };

/** The unpublished draft of the restaurant-wide rule (date rules are never part of it). */
export type VisitWishSettingsDraft = { settings: VisitWishSettings; hasDraft: boolean; baseRevision: number };

/** The API refuses a draft or publish based on an older revision than the live one. */
export const isSettingsConflict = (error: any) =>
  error?.response?.status === 409 || error?.response?.data?.code === 'SETTINGS_CONFLICT';

/** The rule in force on a date (same logic as the API): restaurant default or the date's exception. */
export function effectiveRuleFor(settings: VisitWishSettings, date: string) {
  const exception = (settings.dateRules ?? []).find((rule) => rule.date === date);
  return {
    usePriority: exception?.usePriority ?? settings.usePriority,
    waveSize: exception?.waveSize ?? settings.waveSize,
    waveIntervalMinutes: exception?.waveIntervalMinutes ?? settings.waveIntervalMinutes,
    source: exception ? ('date' as const) : ('restaurant' as const),
  };
}

export const OPEN_STATUSES: VisitWishStatus[] = ['pending_confirmation', 'active', 'paused'];

const base = (restaurantId: string) => `/v03/visitWishes/team/${encodeURIComponent(restaurantId)}`;

export const visitWishApi = {
  /** One wish of the restaurant as the list shows it, wherever it is in the list. */
  get: async (restaurantId: string, wishId: string) =>
    ((await server.get(`${base(restaurantId)}/wishes/${encodeURIComponent(wishId)}`)) as { data: VisitWish }).data,
  /** A page of open wishes only, oldest first; `openCursor` of the answer continues it (null at the end). */
  openPage: async (restaurantId: string, openCursor?: string) =>
    (
      (await server.get(base(restaurantId), { params: { part: 'open', ...(openCursor ? { openCursor } : {}) } })) as {
        data: { wishes: VisitWish[]; openCursor?: string | null };
      }
    ).data,
  /**
   * The first page of open (oldest first) and ended wishes (newest first);
   * with `openCursor` or `endedCursor` the next page of just that part. The
   * answer's cursors are null once everything of that part is loaded.
   */
  listPage: async (restaurantId: string, cursors: { openCursor?: string; endedCursor?: string } = {}) =>
    (
      (await server.get(base(restaurantId), { params: cursors.openCursor || cursors.endedCursor ? cursors : undefined })) as {
        data: { wishes: VisitWish[]; openCursor?: string | null; endedCursor?: string | null };
      }
    ).data,
  settings: async (restaurantId: string) =>
    ((await server.get(`${base(restaurantId)}/settings`)) as { data: LiveVisitWishSettings }).data,
  draft: async (restaurantId: string) =>
    ((await server.get(`${base(restaurantId)}/settings/draft`)) as { data: VisitWishSettingsDraft }).data,
  /** Stores the draft only; nothing changes for guests until it is published. */
  saveDraft: async (restaurantId: string, settings: VisitWishSettings) =>
    ((await server.post(`${base(restaurantId)}/settings/draft`, settings)) as { data: VisitWishSettingsDraft }).data,
  publish: async (restaurantId: string, baseRevision: number) =>
    ((await server.post(`${base(restaurantId)}/settings/publish`, { baseRevision })) as { data: VisitWishSettings })
      .data,
  /** Replaces one date's override, effective immediately (not part of the draft). */
  saveDateRule: async (restaurantId: string, date: string, rule: DateRulePatch) =>
    (
      (await server.patch(`${base(restaurantId)}/settings/date-rules/${encodeURIComponent(date)}`, rule)) as {
        data: VisitWishSettings;
      }
    ).data,
  /** Removes one date's override: the date follows the restaurant standard again. */
  removeDateRule: async (restaurantId: string, date: string) =>
    (
      (await server.delete(`${base(restaurantId)}/settings/date-rules/${encodeURIComponent(date)}`)) as {
        data: VisitWishSettings;
      }
    ).data,
  stop: async (restaurantId: string, wishId: string) =>
    ((await server.post(`${base(restaurantId)}/${encodeURIComponent(wishId)}/stop`)) as { data: VisitWish }).data,
  markExclusive: async (restaurantId: string, wishId: string, durationMinutes: number) =>
    (await server.post(`${base(restaurantId)}/${encodeURIComponent(wishId)}/exclusive`, { durationMinutes })) as unknown,
  unmarkExclusive: async (restaurantId: string, wishId: string) =>
    (await server.post(`${base(restaurantId)}/${encodeURIComponent(wishId)}/exclusive/remove`)) as unknown,
  withdrawOffer: async (restaurantId: string, offerId: string) =>
    (await server.post(`${base(restaurantId)}/offers/${encodeURIComponent(offerId)}/withdraw`)) as unknown,
  setPriority: async (restaurantId: string, wishId: string, priority: number) =>
    (
      (await server.post(`${base(restaurantId)}/${encodeURIComponent(wishId)}/priority`, { priority })) as {
        data: VisitWish;
      }
    ).data,
};

export const apiMessage = (error: any, fallback: string) => error?.response?.data?.message ?? fallback;
